import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Download, Printer, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, PeriodInput, Loading, Empty, StatPill, NativeSelect, StatusBadge, Confirm } from "@/components/common";
import { InvoiceDialog } from "@/pages/tenant/InvoiceDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { currentPeriod, fmtDate, invNo, num, periodLabel, REV_STATE, rupiah } from "@/lib/format";

export default function Invoices() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [period, setPeriod] = useState(sp.get("period") || currentPeriod());
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices", { params: { period, status, q } });
      setRows(data);
      setOpen((o) => (o ? data.find((x) => x.id === o.id) || null : null));
    } catch (e) { toast.error(errMsg(e)); }
  }, [period, status, q]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  const drafts = rows?.filter((r) => r.status === "draft" && !r.blocked_reason) || [];
  const active = rows?.filter((r) => r.status !== "cancelled" && !r.blocked_reason) || [];
  const approveAll = async () => {
    try {
      for (const d of drafts) await api.post(`/invoices/${d.id}/approve`);
      toast.success(`${drafts.length} invoice disahkan`);
      load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };
  return (
    <div>
      <PageHeader sheet="SHEET 05" title="Invoice Akhir" subtitle={`Qty setiap jenis voucher dibulatkan ke kelipatan 5. Periode ${periodLabel(period)}.`}>
        <PeriodInput value={period} onChange={setPeriod} />
        {can("export:run") && <Button variant="outline" onClick={() => downloadCsv("/export/invoices", `invoice-${period}`, { period })} data-testid="invoice-export-btn"><Download className="h-4 w-4 mr-2" />Export</Button>}
        {can("invoice:approve") && drafts.length > 0 && (
          <Confirm title={`Sahkan ${drafts.length} invoice draft?`} description="Semua invoice draft periode ini akan disahkan dan dikunci." onConfirm={approveAll} testId="invoice-approve-all"
            trigger={<Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="invoice-approve-all-btn"><BadgeCheck className="h-4 w-4 mr-2" />Sahkan Semua ({drafts.length})</Button>} />
        )}
        <Button variant="outline" onClick={() => nav(`/cetak?period=${period}`)} data-testid="invoice-go-print"><Printer className="h-4 w-4 mr-2" />Cetak Nota</Button>
      </PageHeader>
      {!rows ? <Loading /> : (
        <div className="space-y-3 fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Jumlah Invoice" value={`${num(active.length)}${rows.length > active.length ? ` (+${rows.length - active.length} batal)` : ""}`} testId="invoice-stat-count" />
            <StatPill label="Draft" value={num(drafts.length)} tone="amber" testId="invoice-stat-draft" />
            <StatPill label="Total Qty" value={num(active.reduce((s, r) => s + r.total_qty, 0))} tone="blue" testId="invoice-stat-qty" />
            <StatPill label="Total Nilai" value={rupiah(active.reduce((s, r) => s + r.total, 0))} tone="green" testId="invoice-stat-total" />
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative w-full sm:w-auto"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Cari no. nota / pelanggan" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full sm:w-64" data-testid="invoice-search-input" /></div>
            <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter">
              <option value="">Semua status</option><option value="draft">Draft</option><option value="approved">Disahkan</option><option value="cancelled">Dibatalkan</option>
            </NativeSelect>
          </div>
          {!rows.length ? <Empty>Belum ada invoice. Buat dari menu Estimasi Pembuatan Voucher.</Empty> : (
            <>
            <div className="md:hidden space-y-2" data-testid="invoice-cards">
              {rows.map((r, i) => (
                <button key={r.id} type="button" onClick={() => setOpen(r)} className={`w-full text-left border border-slate-300 bg-white rounded-sm p-3 space-y-1 active:bg-emerald-50 ${r.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`invoice-card-${i}`}>
                  <div className="flex items-center gap-2"><span className={`font-mono font-semibold text-emerald-800 ${r.status === "cancelled" ? "line-through" : ""}`}>{invNo(r)}</span><span className="flex-1" /><StatusBadge status={r.status} /></div>
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-xs text-slate-600">{r.lines.map((l) => `${l.voucher_name.replace("Voucher ", "")}: ${l.qty}`).join(" · ")}</div>
                  <div className="flex items-center text-xs text-slate-500"><span>{fmtDate(r.date)} · {num(r.total_qty)} voucher · dicetak {r.print_count}x</span><span className="flex-1" /><b className="font-mono text-sm text-slate-900">{rupiah(r.total)}</b></div>
                  {r.revision_state && <div className="text-[10px] text-amber-700">{REV_STATE[r.revision_state]}</div>}
                  {r.blocked_reason && <div className="text-[10px] text-rose-700">PERINGATAN: {r.blocked_reason}</div>}
                </button>
              ))}
            </div>
            <div className="xl-wrap max-h-[60vh] hidden md:block">
              <table className="xl" data-testid="invoice-table">
                <thead><tr><th className="rownum">#</th><th>No. Nota</th><th>Tanggal</th><th>Pelanggan</th><th>Rincian</th><th className="text-right">Total Qty</th><th className="text-right">Total (Rp)</th><th>Status</th><th className="text-right">Cetak</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} className={`cursor-pointer ${r.status === "cancelled" ? "opacity-60 line-through" : ""}`} onClick={() => setOpen(r)} data-testid={`invoice-row-${i}`}>
                      <td className="rownum">{i + 1}</td>
                      <td className="font-mono font-semibold text-emerald-800">{invNo(r)}{r.revision_state && <div className="text-[9px] font-sans text-amber-700" data-testid={`invoice-rev-state-${i}`}>{REV_STATE[r.revision_state]}</div>}{r.blocked_reason && <div className="text-[9px] font-sans text-rose-700" data-testid={`invoice-blocked-${i}`}>PERINGATAN: {r.blocked_reason}</div>}</td>
                      <td className="text-xs">{fmtDate(r.date)}</td>
                      <td className="font-medium">{r.customer_name}</td>
                      <td className="text-xs text-slate-600">{r.lines.map((l) => `${l.voucher_name.replace("Voucher ", "")}: ${l.qty}`).join(" · ")}</td>
                      <td className="num">{num(r.total_qty)}</td>
                      <td className="num">{num(r.total)}</td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="num">{r.print_count}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}
      <InvoiceDialog inv={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
